import { fireReadyEvent } from './events';

export class KatalComponent extends HTMLElement {
  [key: string]: any; //index signature for typescript. property upgrade logic won't compile without this.

  private _propertiesHaveBeenUpgraded = false;
  private _observers = [];
  private _pendingObserverCallbacks = [];

  constructor(private _observedAttributes: string[]) {
    super();
  }

  connectedCallback() {
    window.dispatchEvent(
      new CustomEvent('katal-component-connected', {
        detail: { element: this },
      })
    );

    this.upgradePropertiesToAttributesOnce(this._observedAttributes);
  }

  disconnectedCallback() {
    this._observers.forEach(ob => {
      ob.disconnect();
    });
  }

  normalizeAttributeName(attributeName: string) {
    // 'xxx' stays 'xxx'
    // 'xxx-yyy' becomes 'xxxYyy'
    // 'xxx-yyy-zzz' becomes 'xxxYyyZzz'
    let propertyName = attributeName
      .split('-')
      .map((val, i) => {
        return i === 0 ? val : val.charAt(0).toUpperCase() + val.slice(1);
      })
      .join('');
    return propertyName;
  }

  upgradePropertiesToAttributesOnce(attributeNames: string[]) {
    if (this.propertiesHaveBeenUpgraded) {
      return;
    }

    attributeNames.forEach(attributeName => {
      const propertyName = this.normalizeAttributeName(attributeName);

      if (this.hasOwnProperty(propertyName)) {
        let value = this[propertyName];
        delete this[propertyName];
        this[propertyName] = value; // Calls the setter which sets attribute instead of property
      }
    });

    this._propertiesHaveBeenUpgraded = true;
  }

  protected get propertiesHaveBeenUpgraded() {
    return this._propertiesHaveBeenUpgraded;
  }

  removeOrSetAttribute(attributeName: string, value: any) {
    if (value == null) {
      this.removeAttribute(attributeName);
    } else {
      this.setAttribute(attributeName, value);
    }
  }

  /**
   * Converts the string value into an array by parsing it as JSON and returns it. Returns the value if it's an array. Otherwise, returns an empty array.
   */
  handleArrayAttribute(attributeName: string, value: any) {
    if (typeof value === 'string') {
      try {
        value = JSON.parse(value);
      } catch (e) {
        console.error(
          `${this.tagName} property '${attributeName}' was a string, but not valid JSON.`
        );
        console.error(e);
        value = [];
      }
    }
    if (Array.isArray(value)) {
      return value;
    } else {
      console.error(
        `${this.tagName} property '${attributeName}' was not an array`
      );
      return [];
    }
  }

  isAttributeValueTruthy(value: string) {
    //This allows HTML5 boolean attribute style (<el disabled>), or an explicit "false" to make binding with frameworks easier.
    return value === 'true' || value === '';
  }

  getBooleanAttribute(attributeName: string) {
    return this.isAttributeValueTruthy(this.getAttribute(attributeName));
  }
  setBooleanAttribute(attributeName: string, value: any) {
    if (value === '') {
      value = true;
    } else if (value === 'false') {
      value = false;
    }
    if (value) {
      this.setAttribute(attributeName, value);
    } else {
      this.removeAttribute(attributeName);
    }
  }

  getAttributeOrDefault(attributeName: string, defaultValue: string) {
    return this.getAttribute(attributeName) || defaultValue;
  }

  dispatchCustomEvent<T extends {}>(
    eventType: string,
    detail?: T,
    bubbles: boolean = true
  ) {
    const evt = new CustomEvent(eventType, { bubbles, detail });
    this.dispatchEvent(evt);
  }

  observeChildren(
    parentElement: HTMLElement,
    callback: () => void
  ): MutationObserver {
    const observer = new MutationObserver(
      this._observerCalled.bind(this, parentElement, callback)
    );
    observer.observe(parentElement, { childList: true });
    this._observers.push(observer);
    return observer;
  }

  /**
   * De-dups changes to the element children list to avoid multiple re-renders.
   */
  _observerCalled(parentElement: HTMLElement, callback: () => void): void {
    if (this._pendingObserverCallbacks.length === 0) {
      window.requestAnimationFrame(() => {
        this._pendingObserverCallbacks.forEach(callback => callback.callback());
        this._pendingObserverCallbacks = [];
      });
      this._pendingObserverCallbacks.push({ parentElement, callback });
    } else {
      const alreadyHasCallback = this._pendingObserverCallbacks.some(
        callback => callback.parentElement === parentElement
      );
      if (!alreadyHasCallback) {
        this._pendingObserverCallbacks.push({ parentElement, callback });
      }
    }
  }

  static register(tagName: string, clazz: any) {
    let registerElement = () => {
      if (!window.customElements.get(tagName)) {
        window.customElements.define(tagName, clazz);
        fireReadyEvent(tagName, 'katal-component-ready');
      }
    };

    if (window.customElements) {
      registerElement();
    } else {
      document.addEventListener('WebComponentsReady', registerElement);
    }
  }

  /**
   * Generates a Random ID String of given length (default 10)
   */
  static generateRandomId(length = 10) {
    let base36RandomNumber = Math.random().toString(36);
    //pad with zerors in case math.random returns 0
    base36RandomNumber += '00000000000000000';
    //remove 0. prefix
    base36RandomNumber = base36RandomNumber.slice(2, 18);
    //repeat string to ensure we have enough characters for the desired length
    base36RandomNumber = Array(length + 1).join(base36RandomNumber);
    return base36RandomNumber.slice(0, length);
  }
}
